import React from 'react'
import { App, Button, Typography, notification as staticNotification } from 'antd'
import { useIntl } from 'react-intl'
import * as Sentry from '@sentry/react'

import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { SIGNIN_CANCELLED } from '../../../../util/openid/browser-runtime-v2'
import { trackSignup } from '../analytics'
import { PLATFORM_BRAND } from '../platform-brand'

const { Paragraph } = Typography

// The sign-in flow owns the SIGN_IN_* names and the old page owns the CONNECT_WITH_* ones, so
// this flow reports under its own. Walking away gets its own name rather than a property,
// because an abandoned attempt is a different thing from a broken one.
const PLATFORM_ERROR_EVENT: Record<string, string> = {
  [PlatformEnum.Microsoft]: 'SIGNUP_WITH_MICROSOFT_ERROR',
  [PlatformEnum.Google]: 'SIGNUP_WITH_GOOGLE_ERROR',
  [PlatformEnum.Slack]: 'SIGNUP_WITH_SLACK_ERROR',
}

const PLATFORM_CANCELED_EVENT: Record<string, string> = {
  [PlatformEnum.Microsoft]: 'SIGNUP_WITH_MICROSOFT_CANCELED',
  [PlatformEnum.Google]: 'SIGNUP_WITH_GOOGLE_CANCELED',
  [PlatformEnum.Slack]: 'SIGNUP_WITH_SLACK_CANCELED',
}

// Dismissing the provider tab, declining consent and cancelling a Microsoft prompt all mean the
// same thing to the visitor, so they share one message. Without this branch the raw rejection
// reason is what ends up on screen.
const isCancellation = (errorMessage: string): boolean =>
  errorMessage.includes(SIGNIN_CANCELLED) || errorMessage.includes('access-denied-cancel') || errorMessage.includes('access_denied')

const readErrorMessage = (error): string => {
  if (typeof error === 'string') return error
  if (typeof error === 'object' && error !== null && !(error instanceof Error)) {
    return error.error_description || error.error || JSON.stringify(error, null, 2)
  }
  return String(error)
}

export interface IUsePlatformErrorHandlerParams {
  onRetryMicrosoft: () => void
  /** Walking away is a page-level outcome, so the page says it rather than a corner toast. */
  onCancelled: (platform: string) => void
}

export interface IPlatformErrorHandler {
  handlePlatformError: (error, platform: string) => void
}

/**
 * Turns a failed platform sign-up into something the visitor can act on. Kept apart from the
 * sign-up flows because it is purely about explaining a failure.
 */
export const usePlatformErrorHandler = ({ onRetryMicrosoft, onCancelled }: IUsePlatformErrorHandlerParams): IPlatformErrorHandler => {
  const { formatMessage } = useIntl()
  const app = App.useApp()
  // Outside an <App> provider useApp() hands back empty placeholders, so fall back to the
  // static API rather than calling a method that is not there.
  const notification = typeof app?.notification?.error === 'function' ? app.notification : staticNotification

  const handlePlatformError = (error, platform: string) => {
    const errorMessage = readErrorMessage(error)
    const platformText = PLATFORM_BRAND[platform]?.full ?? platform
    const cancelled = isCancellation(errorMessage)

    const eventName = cancelled ? PLATFORM_CANCELED_EVENT[platform] : PLATFORM_ERROR_EVENT[platform]
    if (eventName) {
      trackSignup(eventName, { errorMessage, platform })
    }

    if (cancelled) {
      onCancelled(platformText)
      return
    }

    // Microsoft only: the copy names Microsoft and the button restarts a Microsoft sign-up.
    // Google emits `invalid_grant` routinely, and used to land here: wrong words, wrong flow.
    const needsMicrosoftConsent = platform === PlatformEnum.Microsoft && (errorMessage.includes('interaction_required') || errorMessage.includes('invalid_grant'))

    if (needsMicrosoftConsent) {
      notification.error({
        message: formatMessage({ id: 'connect.interactionRequiredMSErrorTitle' }),
        description: (
          <div>
            <Paragraph>{formatMessage({ id: 'connect.interactionRequiredMSErrorDescription1' })}</Paragraph>
            <Paragraph>{formatMessage({ id: 'connect.interactionRequiredMSErrorDescription2' })}</Paragraph>
            <Paragraph keyboard copyable>
              {errorMessage}
            </Paragraph>
          </div>
        ),
        duration: 0,
        btn: <Button onClick={onRetryMicrosoft}>{formatMessage({ id: 'connect.login' })}</Button>,
      })
      return
    }

    notification.error({
      message: formatMessage({ id: 'connect.platformErrorTitle' }),
      description: (
        <div>
          <Paragraph>{formatMessage({ id: 'connect.platformErrorDescription1' })}</Paragraph>
          <Paragraph keyboard copyable>
            {errorMessage}
          </Paragraph>
          <Paragraph>{formatMessage({ id: 'connect.platformErrorDescription2' }, { platform: platformText })}</Paragraph>
        </div>
      ),
      duration: 0,
    })

    Sentry.captureException(error)
  }

  return { handlePlatformError }
}
